// What each stream event does to the query cache. The switch is exhaustive over the event names,
// so an event added to the contract without a handler here fails tsc.
import type { QueryClient } from "@tanstack/react-query";

import { invalidateFirstAdDeliveries } from "./first-ad-delivery-events";
import { getGetMyAchievementsQueryKey } from "./generated/endpoints/achievements/achievements";
import { getGetTeamBalanceQueryKey } from "./generated/endpoints/billing/billing";
import {
  getGetJobQueryKey,
  getListJobsQueryKey,
} from "./generated/endpoints/generation/generation";
import { JobStatus } from "./generated/zod";
import { noteProjectHead, noteProjectResync } from "./project-heads";
import type { StreamEvent } from "./stream-event";

/**
 * An edit project's job read: a render (E1: `.../edit-projects/{id}/renders/{job_id}`) or an
 * assistant's answer (#242, G11: `.../edit-projects/{id}/assistant/answers/{job_id}`). Each is a
 * job, so its job's events refetch it. With `jobId`, only that job's read.
 */
function isEditorJobRead(path: string, teamId: string, jobId?: string): boolean {
  if (!path.startsWith(`/api/v1/teams/${teamId}/brands/`)) return false;
  const read = /\/edit-projects\/[^/]+\/(?:renders|assistant\/answers)\/([^/]+)$/.exec(path);
  return read !== null && (jobId === undefined || read[1] === jobId);
}

/**
 * Invalidates every job query of the team (its lists, each job, each edit render and each
 * assistant answer) and the team's balance, which jobs move: after a gap in the stream, any of
 * them may be stale.
 */
export function invalidateTeamJobs(queryClient: QueryClient, teamId: string): Promise<void> {
  const [base] = getListJobsQueryKey(teamId);
  const [balance] = getGetTeamBalanceQueryKey(teamId);
  return queryClient.invalidateQueries({
    predicate: ({ queryKey }) => {
      const path = queryKey[0];
      return (
        typeof path === "string" &&
        (path === base ||
          path.startsWith(`${base}/`) ||
          path === balance ||
          isEditorJobRead(path, teamId))
      );
    },
  });
}

/**
 * What a gap in the stream may have left stale: every job query of the team and its balance, and
 * the person's achievements, whose unlock frames it may have skipped (spec web/achievements,
 * decision 19).
 */
export async function invalidateAfterGap(queryClient: QueryClient, teamId: string): Promise<void> {
  await Promise.all([
    invalidateTeamJobs(queryClient, teamId),
    invalidateFirstAdDeliveries(queryClient, teamId),
    queryClient.invalidateQueries({ queryKey: getGetMyAchievementsQueryKey() }),
  ]);
}

/** A Layerize's list of its AI edits (G13b: `.../jobs/{job_id}/layer-edits`): its items are jobs,
 * the edits, so any job's move may be one of them. */
function isLayerEditList(path: string, teamId: string): boolean {
  const [base] = getListJobsQueryKey(teamId);
  return path.startsWith(`${base}/`) && path.endsWith("/layer-edits");
}

/**
 * How often a team's lists of jobs, its Layerizes' AI edits and its balance are read again on the
 * stream's job moves: at most once a window, the first move's at once and one at the window's end
 * for the moves during it (#935). A template run's steps and a draft's stills are jobs of the team
 * too, dozens a minute, and each move used to read all three again, the next read cancelling the
 * one before. The window is the drafts' poll (2 s), so a list is at most that late; a job's own
 * read still follows its every move.
 */
export const TEAM_REFRESH_MS = 2_000;

/** A team's open window: when it ends, its end's timer, and whether a move came during it. */
interface Window {
  ends: number;
  timer: ReturnType<typeof setTimeout>;
  again: boolean;
}

/** Each query client's teams whose window is open. */
const windows = new WeakMap<QueryClient, Map<string, Window>>();

/**
 * The team's job lists, Layerize edits and balance read again, at most once a window. The window
 * ends by the clock, not only by its timer: a hidden tab's timers can run a minute or more late,
 * and a move after the window's time reads at once rather than wait for them (#935's re-test).
 */
function refreshTeamReads(queryClient: QueryClient, teamId: string): void {
  const open = windows.get(queryClient) ?? new Map<string, Window>();
  windows.set(queryClient, open);
  const current = open.get(teamId);
  if (current !== undefined && Date.now() < current.ends) {
    current.again = true;
    return;
  }
  if (current !== undefined) clearTimeout(current.timer);
  void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) });
  void queryClient.invalidateQueries({
    predicate: ({ queryKey }) => typeof queryKey[0] === "string" && isLayerEditList(queryKey[0], teamId),
  });
  // A job's moves hold, capture or release credits: the header's pill reads the balance.
  void queryClient.invalidateQueries({ queryKey: getGetTeamBalanceQueryKey(teamId) });
  const window: Window = {
    ends: Date.now() + TEAM_REFRESH_MS,
    again: false,
    timer: setTimeout(() => {
      if (open.get(teamId) !== window) return;
      open.delete(teamId);
      if (window.again) refreshTeamReads(queryClient, teamId);
    }, TEAM_REFRESH_MS),
  };
  open.set(teamId, window);
}

function patchStatus(cached: unknown, status: string): unknown {
  if (typeof cached !== "object" || cached === null || !("status" in cached)) return cached;
  return { ...cached, status };
}

export function applyStreamEvent(
  queryClient: QueryClient,
  teamId: string,
  event: StreamEvent,
): void {
  switch (event.name) {
    case "job.transitioned": {
      // A running render's new percent (#269): only its render read moves, so the bell's row
      // reads it; the job's status, list and the balance are as they were.
      const { status: said, progress, output_index: output } = event.data;
      if (said === "running" && progress != null && output === null) {
        void queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            typeof queryKey[0] === "string" && isEditorJobRead(queryKey[0], teamId, event.data.job_id),
        });
        return;
      }
      // Status shows at once; the refetch brings the outputs and everything else. A fetch already
      // in flight is left alone: the invalidation below cancels it and fetches again. A status
      // this build does not know is never written: the refetch brings what the API says.
      const jobKey = getGetJobQueryKey(teamId, event.data.job_id);
      const status = JobStatus.safeParse(event.data.status);
      if (status.success && queryClient.getQueryState(jobKey)?.fetchStatus !== "fetching") {
        queryClient.setQueryData(jobKey, (cached: unknown) => patchStatus(cached, status.data));
      }
      void queryClient.invalidateQueries({ queryKey: jobKey });
      refreshTeamReads(queryClient, teamId);
      void invalidateFirstAdDeliveries(queryClient, teamId);
      // An editor render and an assistant's answer are jobs: their reads follow the job's events.
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          typeof queryKey[0] === "string" && isEditorJobRead(queryKey[0], teamId, event.data.job_id),
      });
      return;
    }
    case "resync":
      void invalidateAfterGap(queryClient, teamId);
      // The saves it skipped send no frame: every open editor reads what changed (spec
      // editor/live-sync-stream.md).
      noteProjectResync(queryClient, teamId);
      return;
    case "edit_project.saved":
      // An editor open on the project reads what changed (spec editor/live-sync-stream.md).
      noteProjectHead(queryClient, teamId, event.data.project_id, event.data.version);
      return;
    case "achievement_unlocked":
      // The board read again brings the unlock: the gift icon counts it and its card plays (spec
      // web/achievements, 4).
      void queryClient.invalidateQueries({ queryKey: getGetMyAchievementsQueryKey() });
      return;
    default:
      event satisfies never;
  }
}
